import { MessageDescriptor } from '@lingui/core';
import { t } from '@lingui/core/macro';
import classNames from 'classnames';
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMedia from 'use-media';

import { Spinner, useElementSize } from 'client-web-sdk';

import { ReactComponent as CloseIcon } from 'src/images/icons/close.svg';

import BaseModal from './BaseModal';
import styles from './Modal.module.css';

type Props = PropsWithChildren<
  ReactModal.Props & {
    contentClassName?: string;
    fullscreenClassName?: string;
    windowedClassName?: string;
    noSizeAnimation?: boolean;
    loading?: boolean | { message: MessageDescriptor };
    alignTopMinHeight?: string;
  } & SizeProps
>;

type SizeProps = { height?: string; minHeight?: string } & (
  | {
      width: string;
      minWidth?: string;
      maxWidth?: string;
    }
  | {
      width?: string;
      minWidth: string;
      maxWidth: string;
    }
);

const WINDOW_OUTER_SPACE = '1rem';

const CONTENT_PADDING_BLOCK = '2.5rem';

const CONTENT_PADDING_INLINE_WINDOWED = '2.5rem';
const CONTENT_PADDING_INLINE_FULLSCREEN_FIX_WIDTH = '1rem';

// For flexible width the horizontal spacing of fullscreen and windowed mode need to be identical,
// since we're measuring height to decide when to go fullscreen. If the horizontal spacing differed,
// the content's height would change when switching between fullscreen and windowed, leading to a
// loop of switching back and forth at certain widths. Note that we use padding to equal padding +
// outer spacing of windowed mode.
const CONTENT_PADDING_INLINE_FULLSCREEN_FLEXIBLE_WIDTH = `calc(${CONTENT_PADDING_INLINE_WINDOWED} + ${WINDOW_OUTER_SPACE})`;

export default function Modal({
  width,
  minWidth,
  maxWidth,
  height,
  minHeight,
  children,
  noSizeAnimation,
  loading,
  contentClassName,
  fullscreenClassName,
  windowedClassName,
  alignTopMinHeight,
  ...props
}: Props) {
  const { sizeRef, showFullscreen, contentPaddingInline, size } = useModalSize({
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
  } as SizeProps);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevActualHeightRef = useRef(size.height);
  /* This needs to run before repaint, else the overflow will flicker visibly for a moment. */
  useLayoutEffect(() => {
    if (prevActualHeightRef.current === size.height) {
      return;
    }
    prevActualHeightRef.current = size.height;
    setIsTransitioning(true);
  }, [size.height]);

  const {
    showComponent: showLoading,
    className: loadingWrapperClassName,
    onTransitionEnd: loadingOnTransitionEnd,
  } = useShowWithAnimation({
    visible: !!loading,
    visibleClassName: styles.visible,
  });

  const [isResizingWindow, setIsResizingWindow] = useState(false);
  useEffect(() => {
    let resizeEndTimeoutId: number | undefined;
    function onResize() {
      window.clearTimeout(resizeEndTimeoutId);
      setIsResizingWindow(true);
      resizeEndTimeoutId = window.setTimeout(() => {
        setIsResizingWindow(false);
      }, 500);
    }
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearTimeout(resizeEndTimeoutId);
    };
  }, []);

  return (
    <BaseModal
      {...props}
      overlayClassName={classNames(styles.Overlay, alignTopMinHeight && styles.alignTop)}
      className={classNames(styles.Modal, props.className, {
        [styles.windowed]: !showFullscreen,
        [styles.fullscreen]: showFullscreen,
        [styles.fixedHeight]: !!height,
        [styles.hasHeight]: !!size.height,
        [styles.isTransitioning]: isTransitioning,
        [styles.animateSize]: !noSizeAnimation && !isResizingWindow,
        [windowedClassName ?? '']: !showFullscreen,
        [fullscreenClassName ?? '']: showFullscreen,
      })}
      htmlOpenClassName={styles.modalOpen}
      style={{
        content: {
          '--content-padding-block': CONTENT_PADDING_BLOCK,
          '--content-padding-inline': contentPaddingInline,
        } as CSSProperties,
        overlay: {
          '--align-top-height':
            alignTopMinHeight ? `max(${alignTopMinHeight}, ${size.height})` : undefined,
        } as CSSProperties,
      }}
    >
      {props.onRequestClose && (
        <button className={styles.CloseButton} onClick={(e) => props.onRequestClose?.(e)}>
          <CloseIcon title={t`Close`} />
        </button>
      )}
      {showLoading && (
        <div
          className={classNames(styles.LoadingWrapper, loadingWrapperClassName)}
          onTransitionEnd={loadingOnTransitionEnd}
        >
          <Spinner size="80px" />
          {typeof loading === 'object' && (
            <div className={styles.LoadingMessage}>{t(loading.message)}</div>
          )}
        </div>
      )}
      <div
        className={classNames(styles.ModalContentSizeWrapper)}
        style={
          showFullscreen ?
            {}
          : {
              width: size.width,
              maxWidth: size.maxWidth,
              minWidth: size.minWidth,
              height: size.height,
            }
        }
        onTransitionEnd={() => {
          setIsTransitioning(false);
        }}
      >
        <div
          className={classNames(styles.ModalContent, contentClassName)}
          style={
            showFullscreen ?
              { maxWidth: size.width ?? size.maxWidth }
            : { minHeight: size.minHeight }
          }
          ref={sizeRef}
        >
          {children}
        </div>
      </div>
    </BaseModal>
  );
}

function useModalSize(props: SizeProps) {
  // Include paddings in height. Height is set on ModalContentSizeWrapper but paddings need to be
  // set on ModalContent, which is inside of ModalContentSizeWrapper. We need to set paddings there
  // to avoid the bottom padding being cut off when the modal becomes scrollable.
  const fixedHeight =
    props.height ? `calc(${props.height} + 2 * ${CONTENT_PADDING_BLOCK})` : undefined;
  const minHeight =
    props.minHeight ? `calc(${props.minHeight} + 2 * ${CONTENT_PADDING_BLOCK})` : undefined;

  const [sizeRef, { height: contentHeight }] = useElementSize();

  // Set height based on either fixed height or content height.
  const height = useMemo(() => {
    if (fixedHeight) {
      return fixedHeight;
    }
    if (contentHeight === 0) {
      return undefined;
    }
    if (minHeight) {
      return `max(${contentHeight}px, ${minHeight})`;
    }
    return `${contentHeight}px`;
  }, [contentHeight, fixedHeight, minHeight]);

  // Calculate the largest possible widths, using the maximum of available paddings. This is used to
  // decide wether to switch to fullscreen mode.
  const widthMaximum =
    props.width ? `calc(${props.width} + 2 * ${CONTENT_PADDING_INLINE_WINDOWED})` : '0px';
  const minWidthMaximum =
    props.minWidth ? `calc(${props.minWidth} + 2 * ${CONTENT_PADDING_INLINE_WINDOWED})` : '0px';

  // Switch to fullscreen when window becomes smaller then modal size in either height or width. We
  // include a small margin (WINDOW_OUTER_SPACE), so that the switch happens before the modal hits
  // the window edge. This is aesthetically more pleasing the bumping into the window edge before
  // going fullscreen.
  const showFullscreen = useMedia(
    `(max-width: calc(max(${widthMaximum}, ${minWidthMaximum}) + 2 * ${WINDOW_OUTER_SPACE})), (max-height: calc(${
      height ?? '0px'
    } + 2 * ${WINDOW_OUTER_SPACE}))`,
  );

  // Determine inline (horizontal) padding. In fullscreen mode we use a smaller padding if possible
  // so we can show more of the content can be visible.
  const contentPaddingInline = useMemo(() => {
    if (showFullscreen) {
      if (props.width) {
        return CONTENT_PADDING_INLINE_FULLSCREEN_FIX_WIDTH;
      }
      return CONTENT_PADDING_INLINE_FULLSCREEN_FLEXIBLE_WIDTH;
    }
    return CONTENT_PADDING_INLINE_WINDOWED;
  }, [props.width, showFullscreen]);

  // Include paddings in width, see above comment on height.
  const width = props.width ? `calc(${props.width} + 2 * ${contentPaddingInline})` : undefined;
  const minWidth =
    props.minWidth ? `calc(${props.minWidth} + 2 * ${contentPaddingInline})` : undefined;
  const maxWidth =
    props.maxWidth ? `calc(${props.maxWidth} + 2 * ${contentPaddingInline})` : undefined;

  return {
    sizeRef,
    contentPaddingInline,
    showFullscreen,
    size: {
      width,
      minWidth,
      maxWidth,
      height,
      minHeight,
    },
  };
}

function useShowWithAnimation({
  visible,
  visibleClassName,
}: {
  visible: boolean;
  visibleClassName: string;
}) {
  const [showComponent, setShowComponent] = useState(visible);
  const [animateShowComponent, setAnimateShowComponent] = useState(visible);

  useEffect(() => {
    if (visible) {
      setShowComponent(true);
      const animateTimeout = window.setTimeout(() => {
        setAnimateShowComponent(true);
      }, 10);
      return () => window.clearTimeout(animateTimeout);
    }
    setAnimateShowComponent(false);
  }, [visible]);

  const onTransitionEnd = useCallback(() => {
    if (!visible) {
      setShowComponent(false);
    }
  }, [visible]);

  return {
    showComponent,
    className: animateShowComponent ? visibleClassName : undefined,
    onTransitionEnd,
  };
}
